import { createSlice } from "@reduxjs/toolkit";
import * as Actions from "../actions";
import {
  dataTorrePesadosReducer,
  roteirosAlertasReducer,
  roteirosCriticidadeReducer,
  safraDiaPedidosReducer,
} from "../reducers";

import { InitialState } from "./models";

const initialState: InitialState = {
  requestGetParams: {
    gruposCodigos: [],
    filiaisIds: [],
    dataCarteira: "",
  },
  roteirosAlertas: {
    data: [],
    isLoading: false,
    error: null,
  },
  roteirosCriticidade: {
    data: [],
    isLoading: false,
    error: null,
  },
  safraDiaPedidos: {
    data: {
      pedidosEmAberto: {
        total: 0,
        variacao: 0,
        percentualDeVariacao: null,
        saldoInicioDoDia: 0,
      },
      pedidosNoPrazo: {
        total: 0,
        variacao: 0,
        percentualDeVariacao: null,
        saldoInicioDoDia: 0,
      },
      pedidosForaDoPrazo: {
        total: 0,
        variacao: 0,
        percentualDeVariacao: null,
        saldoInicioDoDia: 0,
      },
      pedidosVencendoHoje: {
        total: 0,
        variacao: 0,
        percentualDeVariacao: null,
        saldoInicioDoDia: 0,
      },
      previaDiaSeguinte: {
        total: 0,
        variacao: 0,
        percentualDeVariacao: null,
        previaAnterior: 0,
      },
      slaForaDoPrazo: {
        total: 0,
        variacaoDiaAnterior: 0,
      },
    },
    isLoading: false,
    error: null,
  },
  showFiltersForm: false,
  filterOptions: {
    availableFiliais: [],
    availableTransportadoras: [],
    availableCdFiliais: [],
    availableTransportadorasFracionados: [],
  },
  filtersCSV: {
    dataInicial: "",
    dataFinal: "",
    gruposCodigos: "",
    filiaisIds: "",
    roteirosAceitos: true,
    pedidosColetados: "",
    statusEvolucao: "",
    codigoRoteiro: "",
  },
  filtersForm: {
    filiaisIds: [],
    transportadorasCnpjs: [],
    regioes: [],
    isManualSubmit: false,
  },
  headersCsv: [
    { label: "Roteiro", key: "roteiro.codigo", encrypted: false },
    { label: "Motorista", key: "nomeMotorista", encrypted: false },
    { label: "Código Interno", key: "codigoInterno", encrypted: false },
    { label: "Número da Nota Fiscal", key: "notaFiscal", encrypted: false },
    { label: "Número Pedido", key: "numeroPedido", encrypted: false },
    { label: "Criado Em", key: "dataCriacao", encrypted: false },
    {
      label: "Status",
      key: "statusEntrega",
      encrypted: false,
    },
    {
      label: "Código Filial",
      key: "filial.codigoFilial",
      encrypted: false,
    },
    {
      label: "Nome Filial",
      key: "filial.nomeFilial",
      encrypted: false,
    },
    {
      label: "Nome Cliente",
      key: "nomeCliente",
      encrypted: false,
    },
    {
      label: "Transportadora",
      key: "transportadora.nome",
      encrypted: false,
    },
    {
      label: "Status Evolução",
      key: "statusEvolucao",
      encrypted: false,
    },
    {
      label: "Coleta CD",
      key: "statusColeta",
      encrypted: false,
    },
    {
      label: "Tipo",
      key: "tipo",
      encrypted: false,
    },
  ],
  activeSubtab: "",
  configTabs: {
    tabInView: 0,
    tabs: [
      {
        title: "Fracionados - Visão Exceção",
        index: 0,
        active: true,
        name: "visaoExcecao",
      },
    ],
  },
};

const torreFracionadosSlice = createSlice({
  name: "torreFracionadosApp/torreFracionados",
  initialState,
  reducers: {
    changeTab: (state, action) => {
      state.configTabs.tabInView = action.payload;
      if (action.payload === 0) state.configTabs.tabs[1].active = false;
    },
    setActiveSubtabReducer: (state, action) => {
      state.activeSubtab = action.payload;
    },
    setFiltersForm: (state, action) => {
      state.filtersForm = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      Actions.getFiliaisTransportadoras.fulfilled,
      dataTorrePesadosReducer
    );

    // Safra Dia Pedidos
    builder.addCase(Actions.getSafraDiaPedidos.pending, (state) => {
      state.safraDiaPedidos.isLoading = true;
      state.safraDiaPedidos.error = null;
    });

    builder.addCase(
      Actions.getSafraDiaPedidos.fulfilled,
      safraDiaPedidosReducer
    );

    builder.addCase(Actions.getSafraDiaPedidos.rejected, (state, action) => {
      state.safraDiaPedidos.isLoading = false;
      state.safraDiaPedidos.error =
        action.error.message || "Erro ao carregar safra de pedidos";
    });

    // Roteiros Alertas
    builder.addCase(Actions.getRoteirosAlertas.pending, (state) => {
      state.roteirosAlertas.isLoading = true;
      state.roteirosAlertas.error = null;
    });
    builder.addCase(
      Actions.getRoteirosAlertas.fulfilled,
      roteirosAlertasReducer
    );
    builder.addCase(Actions.getRoteirosAlertas.rejected, (state, action) => {
      state.roteirosAlertas.isLoading = false;
      state.roteirosAlertas.error =
        action.error.message || "Erro ao carregar roteiros";
    });

    // Roteiros Criticidade
    builder.addCase(Actions.getRoteirosCriticidade.pending, (state) => {
      state.roteirosCriticidade.isLoading = true;
      state.roteirosCriticidade.error = null;
    });
    builder.addCase(
      Actions.getRoteirosCriticidade.fulfilled,
      roteirosCriticidadeReducer
    );
    builder.addCase(
      Actions.getRoteirosCriticidade.rejected,
      (state, action) => {
        state.roteirosCriticidade.isLoading = false;
        state.roteirosCriticidade.error =
          action.error.message || "Erro ao carregar criticidades dos roteiros";
      }
    );

    // Filiais
    builder.addCase(Actions.getFiliais.pending, (state) => {
      // Pode adicionar loading se necessário
    });
    builder.addCase(Actions.getFiliais.fulfilled, (state, action) => {
      state.filterOptions.availableCdFiliais = action.payload;
    });
    builder.addCase(Actions.getFiliais.rejected, (state, action) => {
      console.error("Erro ao carregar filiais:", action.error.message);
    });

    // Transportadoras
    builder.addCase(Actions.getTransportadoras.pending, (state) => {
      // Pode adicionar loading se necessário
    });
    builder.addCase(Actions.getTransportadoras.fulfilled, (state, action) => {
      state.filterOptions.availableTransportadorasFracionados = action.payload;
    });
    builder.addCase(Actions.getTransportadoras.rejected, (state, action) => {
      console.error("Erro ao carregar transportadoras:", action.error.message);
    });
  },
});

export const { changeTab, setActiveSubtabReducer, setFiltersForm } =
  torreFracionadosSlice.actions;

export default torreFracionadosSlice.reducer;
